import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query, queryAll } from 'lit/decorators.js';
import './renderers/text-renderer';
import './renderers/chart-renderer';
import type { BiaTextRenderer } from './renderers/text-renderer';
import type { BiaChartRenderer } from './renderers/chart-renderer';
import { exportTablesToXlsx } from '../utils/xlsx';
import { downloadDataUrl, safeFileName } from '../utils/download';

// Naive but reliable: a markdown GFM table contains a separator row of the form
// | --- | ---:| etc. between the header and body.
const MD_TABLE_RE = /\|[\s:|-]*-{2,}[\s:|-]*\|/;

@customElement('bia-chat-bubble')
export class BiaChatBubble extends LitElement {
  static styles = css`
    :host {
      display: flex;
      flex-direction: column;
      margin: 14px 0;
      min-width: 0;
      max-width: 100%;
      /* Opacity-only animation — never use transform here, it would hijack
         position:fixed descendants (chart maximize, dialogs, etc.) */
      animation: bubble-in 240ms ease-out both;
    }
    @keyframes bubble-in {
      from { opacity: 0; }
      to   { opacity: 1; }
    }
    :host([role='user']) {
      align-items: flex-end;
    }
    :host([role='assistant']),
    :host([role='error']) {
      align-items: flex-start;
    }
    .label {
      font-family: var(--bia-font-display, Georgia, serif);
      font-style: italic;
      font-weight: 500;
      font-size: 13px;
      letter-spacing: -0.01em;
      color: var(--bia-text-muted, #6b6b66);
      margin-bottom: 6px;
      padding-left: 2px;
    }
    .bubble {
      font-family: var(--bia-font-family, sans-serif);
      line-height: 1.55;
      word-wrap: break-word;
      overflow-wrap: anywhere;
      font-size: 14px;
      min-width: 0;
    }
    .user {
      display: inline-block;
      max-width: 82%;
      padding: 10px 14px;
      border-radius: 16px 16px 4px 16px;
      background: var(--bia-user-bubble, #1a1a2e);
      color: var(--bia-user-bubble-text, #fafaf7);
      font-weight: 500;
    }
    .assistant {
      width: 100%;
      max-width: 100%;
      padding: 0 2px;
      color: var(--bia-text, #0f0f0e);
      box-sizing: border-box;
    }
    .error {
      display: inline-block;
      max-width: 90%;
      padding: 10px 14px;
      border-radius: 12px;
      background: var(--bia-danger-bg, #fef2f2);
      color: var(--bia-danger, #b91c1c);
      border: 1px solid #fecaca;
      font-size: 13px;
    }
    .actions {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      margin-top: 12px;
      padding-left: 2px;
    }
    .actions button {
      background: transparent;
      border: 1px solid var(--bia-border, #e8e5dc);
      color: var(--bia-text-muted, #6b6b66);
      border-radius: 999px;
      padding: 4px 12px;
      font-size: 11px;
      font-family: var(--bia-font-family, sans-serif);
      font-weight: 500;
      letter-spacing: 0.02em;
      cursor: pointer;
      line-height: 1.4;
      display: inline-flex;
      align-items: center;
      gap: 5px;
      transition: background 140ms ease, border-color 140ms ease, color 140ms ease;
    }
    .actions button:hover {
      background: var(--bia-text, #0f0f0e);
      border-color: var(--bia-text, #0f0f0e);
      color: var(--bia-primary-text, #fafaf7);
    }
    .actions button:active {
      transform: translateY(1px);
    }
    .actions svg {
      width: 12px;
      height: 12px;
      stroke: currentColor;
      fill: none;
      stroke-width: 2;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  `;

  @property({ type: String, reflect: true }) role: 'user' | 'assistant' | 'error' = 'assistant';
  @property({ type: String }) content = '';
  @property({ type: String }) envelopeType: 'text' | 'chart' | 'table' = 'text';
  @property({ type: Object }) data: unknown = null;
  @property({ type: Object }) charts: Record<string, unknown>[] | null = null;

  @query('bia-text-renderer') private textRendererEl?: BiaTextRenderer;
  @query('bia-chart-renderer') private chartRendererEl?: BiaChartRenderer;
  @queryAll('bia-chart-renderer') private allChartRendererEls!: NodeListOf<BiaChartRenderer>;

  /**
   * Effective chart list: prefer `charts` (multi), fall back to `data`
   * wrapped in a single-element array (single). Returns an empty array
   * when the bubble has no chart payload.
   */
  private get effectiveCharts(): Record<string, unknown>[] {
    if (Array.isArray(this.charts) && this.charts.length > 0) {
      return this.charts;
    }
    if (this.data && typeof this.data === 'object') {
      return [this.data as Record<string, unknown>];
    }
    return [];
  }

  private get hasTablesInContent(): boolean {
    return MD_TABLE_RE.test(this.content);
  }

  private exportChart(format: 'png' | 'jpg', index = 0): void {
    // When there's a single chart, `chartRendererEl` is the only instance
    // and `allChartRendererEls` has length 1 — both paths return the same
    // node. When multi, index picks which one to export.
    const renderers = Array.from(this.allChartRendererEls ?? []);
    const target = renderers[index] ?? this.chartRendererEl;
    const url = target?.exportImage(format);
    if (!url) return;
    const suffix = renderers.length > 1 ? `-${index + 1}` : '';
    downloadDataUrl(url, safeFileName(`bia-grafico${suffix}`, format));
  }

  private async exportTablesXlsx(): Promise<void> {
    const tables = this.textRendererEl?.getTables() ?? [];
    if (!tables.length) return;
    await exportTablesToXlsx(tables, {
      fileName: safeFileName('bia-tabela', 'xlsx'),
    });
  }

  private renderActions() {
    if (this.role !== 'assistant') return nothing;
    const charts = this.effectiveCharts;
    const showChartActions = this.envelopeType === 'chart' && charts.length > 0;
    const showTableAction = this.hasTablesInContent;
    if (!showChartActions && !showTableAction) return nothing;
    const arrow = html`<svg viewBox="0 0 24 24"><path d="M12 4v14M5 13l7 7 7-7"/></svg>`;
    // Single-chart path keeps the original "PNG" / "JPG" buttons (no
    // numeric suffix). Multi-chart path labels each button with its
    // 1-based index so the user knows which chart is being exported.
    const chartButtons =
      charts.length === 1
        ? html`
            <button @click=${() => this.exportChart('png', 0)} title="Baixar como PNG">
              ${arrow} PNG
            </button>
            <button @click=${() => this.exportChart('jpg', 0)} title="Baixar como JPG">
              ${arrow} JPG
            </button>
          `
        : charts.map(
            (_, i) => html`
              <button
                @click=${() => this.exportChart('png', i)}
                title="Baixar gráfico ${i + 1} como PNG"
              >
                ${arrow} PNG ${i + 1}
              </button>
              <button
                @click=${() => this.exportChart('jpg', i)}
                title="Baixar gráfico ${i + 1} como JPG"
              >
                ${arrow} JPG ${i + 1}
              </button>
            `,
          );
    return html`
      <div class="actions">
        ${showChartActions ? chartButtons : ''}
        ${showTableAction
          ? html`
              <button @click=${this.exportTablesXlsx} title="Baixar tabela em Excel">
                ${arrow} Excel
              </button>
            `
          : ''}
      </div>
    `;
  }

  render() {
    const classes = `bubble ${this.role}`;
    if (this.role === 'assistant') {
      const charts = this.envelopeType === 'chart' ? this.effectiveCharts : [];
      return html`
        <span class="label">BIA</span>
        <div class="${classes}">
          ${this.content
            ? html`<bia-text-renderer .content=${this.content}></bia-text-renderer>`
            : ''}
          ${charts.map(
            (c) => html`<bia-chart-renderer .data=${c}></bia-chart-renderer>`,
          )}
        </div>
        ${this.renderActions()}
      `;
    }
    return html`<div class="${classes}">${this.content}</div>`;
  }
}
